import Icon from 'components/AppIcon';
import { useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

const MonthYearNavigation = ({
  selectedMonth,
  selectedYear,
  setSelectedMonth,
  setSelectedYear,
  onMonthChange,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentYear = new Date()?.getFullYear();
  const currentMonth = new Date()?.getMonth() + 1;

  const setDataToSearchParams = useCallback(
    (params) => {
      setSearchParams({
        ...Object.fromEntries(searchParams),
        ...params,
      });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    if (searchParams?.get('month') || searchParams?.get('year')) {
      setSelectedMonth(parseInt(searchParams?.get('month')));
      setSelectedYear(parseInt(searchParams?.get('year')));
      onMonthChange?.(parseInt(searchParams?.get('year')), parseInt(searchParams?.get('month')));
    } else {
      setSelectedMonth(currentMonth);
      setSelectedYear(currentYear);
      setDataToSearchParams({ month: currentMonth, year: currentYear });
      onMonthChange?.(currentYear, currentMonth);
    }
  }, [
    searchParams,
    setSelectedMonth,
    setSelectedYear,
    setSearchParams,
    currentMonth,
    currentYear,
    onMonthChange,
    setDataToSearchParams,
  ]);

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    setDataToSearchParams({ month: month, year: selectedYear });
    onMonthChange?.(selectedYear, month);
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    setDataToSearchParams({ month: selectedMonth, year: year });
    onMonthChange?.(year, selectedMonth);
  };

  const handlePreviousMonth = () => {
    let newMonth = selectedMonth - 1;
    let newYear = selectedYear;

    if (newMonth < 1) {
      newMonth = 12;
      newYear = selectedYear - 1;
    }

    if (newYear >= 2010) {
      setSelectedMonth(newMonth);
      setSelectedYear(newYear);
      setDataToSearchParams({ month: newMonth, year: newYear });
      onMonthChange?.(newYear, newMonth);
    }
  };

  const handleNextMonth = () => {
    let newMonth = selectedMonth + 1;
    let newYear = selectedYear;

    if (newMonth > 12) {
      newMonth = 1;
      newYear = selectedYear + 1;
    }

    if (newYear < currentYear || (newYear === currentYear && newMonth <= currentMonth)) {
      setSelectedMonth(newMonth);
      setSelectedYear(newYear);
      setDataToSearchParams({ month: newMonth, year: newYear });
      onMonthChange?.(newYear, newMonth);
    }
  };

  const handleToday = () => {
    setSelectedMonth(currentMonth);
    setSelectedYear(currentYear);
    setDataToSearchParams({ month: currentMonth, year: currentYear });
    onMonthChange?.(currentYear, currentMonth);
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ].filter((month) => {
    if (selectedYear === currentYear) {
      return month.value <= currentMonth;
    }
    return selectedYear < currentYear;
  });

  const years = Array.from({ length: currentYear - 2010 + 1 }, (_, i) => currentYear - i).filter(
    (year) => year >= 2010 && year <= currentYear,
  );

  return (
    <div className="flex items-center space-x-2 sm:space-x-4">
      {(currentMonth !== selectedMonth || selectedYear !== currentYear) && (
        <button
          onClick={handleToday}
          className="px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium transition-all duration-200 inline-flex items-center space-x-2 border border-gray-300 hover:border-gray-400"
          title="Today"
        >
          <Icon name="CircleX" size={14} />
          <span>Reset</span>
        </button>
      )}

      <button
        onClick={handlePreviousMonth}
        disabled={selectedYear === 2010 && selectedMonth === 1}
        className="p-2 rounded-lg border border-border bg-background hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
        title="Previous Month"
      >
        <Icon name="ChevronLeft" size={16} />
      </button>

      <div className="flex items-center space-x-3">
        <div className="relative">
          <select
            value={selectedMonth}
            onChange={(e) => handleMonthChange(parseInt(e?.target?.value))}
            className="appearance-none px-4 py-2 pr-8 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 bg-background text-text-primary text-sm font-medium min-w-[120px]"
            style={{ backgroundImage: 'none' }}
          >
            {months?.map((month) => (
              <option key={month?.value} value={month?.value}>
                {month?.label}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <Icon name="ChevronDown" size={16} color="var(--color-text-secondary)" />
          </div>
        </div>

        <div className="relative">
          <select
            value={selectedYear}
            onChange={(e) => handleYearChange(parseInt(e?.target?.value))}
            className="appearance-none px-4 py-2 pr-8 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 bg-background text-text-primary text-sm font-medium min-w-[80px]"
            style={{ backgroundImage: 'none' }}
          >
            {years?.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <Icon name="ChevronDown" size={16} color="var(--color-text-secondary)" />
          </div>
        </div>
      </div>

      <button
        onClick={handleNextMonth}
        disabled={selectedYear === currentYear && selectedMonth === currentMonth}
        className="p-2 rounded-lg border border-border bg-background hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
        title="Next Month"
      >
        <Icon name="ChevronRight" size={16} />
      </button>
    </div>
  );
};

export default MonthYearNavigation;
